import { Icon } from "@iconify/react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { UploadSource } from "@rms-types/lib/s3/interface";
import { Attachment } from "@rms-types/modules/attachment/entities/attachment";
import { AttachmentPathParams } from "@rms-types/modules/attachment/interface";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Fragment, ReactNode } from "react";
import { DropzoneOptions, FileRejection } from "react-dropzone";
import { FieldValues, Path, PathValue, get, useFormContext } from "react-hook-form";

import { DropArea, TrackingFilesList } from "@/components";
import { ContentBox } from "@/components/elements/content-box";
import { ERROR_BOUNDARY_MESSAGE } from "@/constants";
import {
  AttachmentDisplay,
  useBulkCreateAttachment,
  useCreateAttachmentGroup,
  useDeleteAttachment,
  useGetAttachmentsById,
} from "@/features";
import { useFileUpload } from "@/hooks";

export type FormFileValue = null | number;

export interface FormFileProps<TFormValues extends FieldValues> extends DropzoneOptions {
  name: Path<TFormValues>;
  uploadSource?: UploadSource;
  pathParams?: AttachmentPathParams;
  label?: ReactNode;
  helperText?: ReactNode;
  disabled?: boolean;
  renderDropzone?: (options: DropzoneOptions, hasError: boolean) => ReactNode;
}

export function FormFile<TFormValues extends FieldValues>({
  name,
  uploadSource = "user_document",
  pathParams,
  label = "",
  helperText = "",
  disabled = false,
  renderDropzone,
  ...props
}: FormFileProps<TFormValues>) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<TFormValues>();
  const value = watch(name);
  const error = get(errors, name);

  const { loadingFiles, failedFiles, upload } = useFileUpload();

  const { createBulkAttachment } = useBulkCreateAttachment();
  const { createAttachmentGroup } = useCreateAttachmentGroup();
  const { attachments, attachmentQueryKey, refetchAttachments } = useGetAttachmentsById(value);
  const { deleteAttachment, isDeleteAttachmentPending } = useDeleteAttachment({
    onSuccess: (_, variables) => {
      const queryData = queryClient.getQueryData(attachmentQueryKey);

      if (Array.isArray(queryData)) {
        queryClient.setQueryData<Attachment[]>(
          attachmentQueryKey,
          queryData.filter((item) => item.id !== variables.id)
        );
      }
    },
  });

  const handleDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    try {
      let attachmentGroupId: number;

      if (value) {
        attachmentGroupId = value;
      } else {
        createAttachmentGroup({
          onSuccess: (data) => {
            setValue(name, data.id as PathValue<TFormValues, Path<TFormValues>>);
            attachmentGroupId = data.id;
          },
        });
      }

      const uploadedFiles = await upload([...acceptedFiles, ...fileRejections], uploadSource, pathParams);
      createBulkAttachment(
        { payload: uploadedFiles.map((uploadedFile) => ({ ...uploadedFile, attachment_group_id: attachmentGroupId })) },
        { onSuccess: () => refetchAttachments() }
      );
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar("Failed to upload files", { variant: "error" });
    }
  };

  const dropzoneOptions: DropzoneOptions = {
    disabled,
    onDrop: handleDrop,
    ...props,
  };

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Box>
        {label && (
          <FormLabel disabled={disabled} sx={{ mb: 1 }} component="div">
            {label}
          </FormLabel>
        )}

        <Stack direction="column" spacing={2}>
          {/* Drop Area */}
          {renderDropzone ? (
            renderDropzone(dropzoneOptions, !!error)
          ) : (
            <DropArea hasError={!!error} {...dropzoneOptions} />
          )}

          {error && error.message ? (
            <FormHelperText sx={{ mx: "14px !important", mt: "4px !important" }} error>
              {error.message}
            </FormHelperText>
          ) : (
            helperText && (
              <FormHelperText sx={{ mx: "14px !important", mt: "4px !important" }}>{helperText}</FormHelperText>
            )
          )}

          {/* Tracking Files */}
          <TrackingFilesList loadingFiles={loadingFiles} failedFiles={failedFiles} />

          {/** Fetched files */}
          {attachments.length > 0 && (
            <ContentBox sx={{ px: 0 }}>
              <Stack direction="column" spacing={2}>
                {attachments.map((attachment, index, array) => {
                  return (
                    <Fragment key={attachment.id}>
                      <Stack px={2} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <AttachmentDisplay name={attachment.name} />

                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            disabled={isDeleteAttachmentPending}
                            onClick={() => deleteAttachment({ id: attachment.id })}
                          >
                            <Icon icon="ph:trash-fill" fontSize={22} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {index < array.length - 1 && <Divider />}
                    </Fragment>
                  );
                })}
              </Stack>
            </ContentBox>
          )}
        </Stack>
      </Box>
    </Sentry.ErrorBoundary>
  );
}
